/**
 * v6.5.1 — Remove Account (admin) confirmation modal.
 *
 * Safety rails enforced by the UI:
 *   • Admin must type the account slug EXACTLY to enable the final
 *     "Remove account" button (match is case-insensitive, trim-only).
 *   • If the backend returns 409 (pipeline running or queued), the modal
 *     surfaces the message and blocks confirmation.
 *   • If the backend returns 400 (slug mismatch), the toast explains.
 *   • The modal shows preserved-data counts so operator sees what IS NOT
 *     being deleted. "Remove" is a soft archive; nothing historical is
 *     destroyed.
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function RemoveAccountModal({ open, onOpenChange, account, healthEntry, onSuccess }) {
  const [typedSlug, setTypedSlug] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pipelineSnap, setPipelineSnap] = useState({ active: null, queue: [] });

  // Fetch pipeline state once when the modal opens. Self-contained — we
  // don't rely on the PipelineHealthWidget's state because that widget
  // is encapsulated. The backend endpoint is the source of truth and
  // will 409 anyway, but this pre-check gives the operator a quiet
  // message instead of a failed submit.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/admin/pipeline/status");
        if (!cancelled) setPipelineSnap({ active: data?.active || null, queue: data?.queue || [] });
      } catch {
        // Non-fatal — leave snap empty; backend will still 409 if needed.
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) { setTypedSlug(""); setReason(""); setSubmitting(false); }
  }, [open]);

  if (!account) return null;

  const slug = account.id || "";
  const displayName = account.name || slug;
  const slugMatches = typedSlug.trim().toLowerCase() === slug.toLowerCase();

  // Is the pipeline actively running for this account?
  const activeAcc = (pipelineSnap.active?.account || "").toLowerCase();
  const isRunning = activeAcc && (activeAcc === slug.toLowerCase() || activeAcc === displayName.toLowerCase());

  // Is this account sitting in the queue?
  const isQueued = (pipelineSnap.queue || []).some((e) => {
    const acc = (e.account || "").toLowerCase();
    return acc === slug.toLowerCase() || acc === displayName.toLowerCase();
  });

  const blockReason = isRunning
    ? "Generation is currently running for this account. Wait for it to finish, then remove the account."
    : isQueued
      ? "This account has a queued generation job. Let the queue clear first."
      : null;

  const confirmDisabled = submitting || !slugMatches || !!blockReason;

  async function handleRemove() {
    if (confirmDisabled) return;
    setSubmitting(true);
    try {
      const { data } = await API.post(`/admin/accounts/${slug}/remove`, {
        confirm_slug: slug,
        reason: reason.trim() || null,
      });
      const preserved = data?.preserved || {};
      const effects = data?.effects || {};
      toast.success(
        `${displayName} archived. Historical data preserved.` +
          (effects.removed_from_followed_accounts_count
            ? ` ${effects.removed_from_followed_accounts_count} user intake(s) cleaned up.`
            : "")
      );
      onSuccess?.({ account: data?.account, preserved, effects });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Remove failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-zinc-200 max-w-lg" data-testid="remove-account-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-950" data-testid="remove-account-modal-title">
            Remove {displayName}?
          </DialogTitle>
          <DialogDescription className="text-zinc-700 text-sm pt-2 space-y-2">
            <div>
              This removes the account from active Kalib AI operations and user-facing account selection.
            </div>
            <div>
              Existing harvests, Reveals, citations, QA audits, generation logs, feedback, and source history will be preserved. Scheduled refreshes will be disabled. Users following this account will no longer see it as an active account.
            </div>
            <div className="text-xs text-zinc-600">
              Reversible via <span className="font-mono">POST /api/admin/accounts/{slug}/restore</span>. Not a hard delete.
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Metadata panel */}
          <div className="border border-zinc-200 p-3 space-y-1 bg-zinc-50 text-[12px]">
            <div className="flex justify-between"><span className="text-zinc-600">Display name</span><span className="font-medium">{displayName}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Slug</span><span className="font-mono">{slug}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Current status</span><span className="font-medium">{account.status || "?"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Weekly refresh</span><span>{account.weekly_run_day || "not scheduled"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Latest reveal</span><span className="font-mono">{formatTimestamp(healthEntry?.reveal_generated_at)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Latest scheduled run</span><span className="font-mono">{formatTimestamp(healthEntry?.last_scheduled_run_at)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Pipeline state</span><span data-testid="remove-account-pipeline-state">{isRunning ? "running" : isQueued ? "queued" : "idle"}</span></div>
          </div>

          {blockReason && (
            <div className="border border-red-200 bg-red-50 text-red-800 p-3 text-[12px]" data-testid="remove-account-block-reason">
              {blockReason}
            </div>
          )}

          {/* Typed confirmation */}
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1" htmlFor="remove-account-confirm-slug">
              Type the slug <span className="font-mono text-zinc-900 normal-case tracking-normal">{slug}</span> to confirm
            </label>
            <Input
              id="remove-account-confirm-slug"
              value={typedSlug}
              onChange={(e) => setTypedSlug(e.target.value)}
              placeholder={slug}
              className="rounded-none border-zinc-200 focus:ring-red-600 focus:border-red-600 font-mono"
              data-testid="remove-account-confirm-input"
              disabled={!!blockReason}
            />
            {typedSlug && !slugMatches && (
              <p className="mt-1 text-[11px] text-red-600">Slug doesn't match yet.</p>
            )}
          </div>

          {/* Optional reason */}
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1" htmlFor="remove-account-reason">
              Reason (optional)
            </label>
            <Input
              id="remove-account-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate account / retiring vendor / archiving for now"
              className="rounded-none border-zinc-200 focus:ring-amber-700 focus:border-amber-700"
              data-testid="remove-account-reason-input"
              disabled={!!blockReason}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none border-zinc-200"
            data-testid="remove-account-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleRemove}
            disabled={confirmDisabled}
            className="rounded-none bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            data-testid="remove-account-confirm-btn"
          >
            {submitting ? "Removing…" : "Remove account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
